import React, { useEffect, useRef } from 'react';

export type HorizontalScrollTabItem = {
  value: string;
  label: string;
  domId?: string;
  id?: string;
  testId?: string;
};

export type HorizontalScrollTabsData = {
  items?: HorizontalScrollTabItem[];
  activeValue?: string;
  onTabClick?: (value: string) => void;
  ariaLabel?: string;
  className?: string;
};

type Props = {
  data: HorizontalScrollTabsData;
};

function scrollActiveTabIntoView(
  scroller: HTMLElement | null,
  active: HTMLElement | null,
) {
  if (!scroller || !active) {
    return;
  }
  const scrollerRect = scroller.getBoundingClientRect();
  const activeRect = active.getBoundingClientRect();
  const isVisible =
    activeRect.left >= scrollerRect.left && activeRect.right <= scrollerRect.right;
  if (isVisible) {
    return;
  }
  const delta =
    activeRect.left - scrollerRect.left - (scrollerRect.width - activeRect.width) / 2;
  scroller.scrollBy({ left: delta, behavior: 'smooth' });
}

function resolveTabIds(item: HorizontalScrollTabItem) {
  const domId = item.id ?? (item.domId ? `mobile-header-tab-${item.domId}` : undefined);
  const testId = item.testId ?? (item.domId ? `horizontal-scroll-tab-${item.domId}` : undefined);
  return { domId, testId };
}

type TabProps = {
  item: HorizontalScrollTabItem;
  isActive: boolean;
  activeRef: React.RefObject<HTMLButtonElement>;
  onSelect: (value: string) => void;
};

function Tab({ item, isActive, activeRef, onSelect }: TabProps) {
  const className = isActive
    ? 'horizontal-scroll-tabs__tab horizontal-scroll-tabs__tab--active'
    : 'horizontal-scroll-tabs__tab';
  const { domId, testId } = resolveTabIds(item);
  return (
    <button
      ref={isActive ? activeRef : null}
      type="button"
      role="tab"
      id={domId}
      aria-selected={isActive}
      tabIndex={isActive ? 0 : -1}
      className={className}
      onClick={() => onSelect(item.value)}
      data-testid={testId}
    >
      <span className="horizontal-scroll-tabs__label">{item.label}</span>
    </button>
  );
}

export function HorizontalScrollTabs({ data }: Props) {
  const items = data?.items ?? [];
  const activeValue = data?.activeValue ?? '';
  const onTabClick = data?.onTabClick;
  const ariaLabel = data?.ariaLabel;
  const extraClassName = data?.className;

  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const activeRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    scrollActiveTabIntoView(scrollerRef.current, activeRef.current);
  }, [activeValue, items.length]);

  if (items.length < 2) {
    return null;
  }

  const handleSelect = (value: string) => {
    if (value !== activeValue && onTabClick) {
      onTabClick(value);
    }
  };

  const navClassName = extraClassName
    ? `horizontal-scroll-tabs ${extraClassName}`
    : 'horizontal-scroll-tabs';

  return (
    <nav
      className={navClassName}
      aria-label={ariaLabel || undefined}
      data-testid="horizontal-scroll-tabs"
    >
      <div className="horizontal-scroll-tabs__scroller" ref={scrollerRef} role="tablist">
        {items.map((item) => (
          <Tab
            key={item.value}
            item={item}
            isActive={item.value === activeValue}
            activeRef={activeRef}
            onSelect={handleSelect}
          />
        ))}
      </div>
    </nav>
  );
}
